import React, { useState, useEffect, useCallback } from 'react';
import { MantineProvider } from '@mantine/core';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import OrganizationSelector from './components/OrganizationSelector';
import { isSuperUser, decodeJWT } from './utils/jwtUtils';
import { API_ENDPOINTS } from './constants/api';
import './App.css';

// Global theme configuration
const theme = {
  components: {
    Table: {
      defaultProps: {
        striped: true,
        highlightOnHover: true,
        withBorder: true,
        withColumnBorders: true,
        horizontalSpacing: 'md',
      },
      styles: {
        root: {
          '--mantine-table-row-height': '24px',
          '--mantine-table-header-height': 'auto',
        },
      },
    },
  },
  // Global defaults for all Mantine React Table columns
  other: {
    MantineReactTable: {
      defaultColumnProps: {
        enableSorting: true,
        enableColumnFilter: true,
        enableHiding: false,
        enableColumnDragging: false,
        size: 120,
      },
    },
  },
};

interface User {
  id: string;
  name: string;
  email: string;
  organization?: string; // Only for regular organization users
  token: string;
  is_super_user: boolean;
  user_type?: number; // 0 = Regular user, 1 = SuperUser, 2 = ProtocolManager
  assigned_organizations?: any[]; // For ProtocolManagers
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
  const [selectedOrganizationName, setSelectedOrganizationName] = useState<string | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<'idle' | 'verifying' | 'success' | 'error'>('idle');
  const [verificationMessage, setVerificationMessage] = useState('');

  const handleEmailVerification = useCallback(async (token: string) => {
    console.log('📧 handleEmailVerification called with token:', token.substring(0, 10) + '...');
    setVerificationStatus('verifying');
    setAuthError('');
    console.log('✅ Verification status set to "verifying"');
    console.log('Starting email verification with token:', token.substring(0, 10) + '...');
    try {
      const response = await fetch(`${API_ENDPOINTS.AUTH}/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      
      console.log('Verification response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Verification failed' }));
        console.error('Verification failed:', errorData);
        throw new Error(errorData.message || 'Verification failed');
      }
      
      const data = await response.json();
      console.log('Verification successful, received user data');
      const decodedToken = decodeJWT(data.token);
      
      // Store auth token and user data
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('userData', JSON.stringify(data.user));
      
      const isSpecialUser = decodedToken?.user_type === 1 || decodedToken?.user_type === 2;
      
      setUser({
        id: isSpecialUser ? data.user.user_id : data.user.organization_id,
        name: `${data.user.first_name} ${data.user.last_name}`,
        email: data.user.email,
        organization: data.user.organization,
        token: data.token,
        is_super_user: decodedToken?.is_super_user || false,
        user_type: decodedToken?.user_type || 0,
        assigned_organizations: data.user.assigned_organizations || [],
      });
      setIsAuthenticated(true);
      setVerificationStatus('success');
      setVerificationMessage('Email verified successfully! You are now logged in.');
      
      // Clean up URL after successful verification
      window.history.replaceState({}, '', '/');
      
      // Set organization selection based on user type
      if (decodedToken?.is_super_user || decodedToken?.user_type === 1) {
        setSelectedOrganizationId(null);
        setSelectedOrganizationName(null);
      } else if (decodedToken?.user_type === 2) {
        setSelectedOrganizationId(null);
        setSelectedOrganizationName(null);
      } else {
        setSelectedOrganizationId(data.user.organization_id);
        setSelectedOrganizationName(data.user.organization);
      }
    } catch (error) {
      console.error('Verification error:', error);
      console.error('Verification error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      setVerificationStatus('error');
      setVerificationMessage(error instanceof Error ? error.message : 'Verification failed. Please try again or request a new verification email.');
      setIsAuthenticated(false); // Make sure we show error message
      // Clean up URL even on error so user can try again
      window.history.replaceState({}, '', '/');
    }
  }, []);

  useEffect(() => {
    // Check for email verification token in URL (works for both /verify-email?token=... and ?token=...)
    console.log('=== EMAIL VERIFICATION DEBUG START ===');
    console.log('App.tsx useEffect - Full URL:', window.location.href);
    console.log('App.tsx useEffect - Path:', window.location.pathname);
    console.log('App.tsx useEffect - Search:', window.location.search);
    console.log('App.tsx useEffect - Hash:', window.location.hash);
    
    // Try multiple ways to extract token
    const urlParams = new URLSearchParams(window.location.search);
    let verificationToken = urlParams.get('token');
    
    // Also check hash in case it's there
    if (!verificationToken && window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      verificationToken = hashParams.get('token');
    }
    
    console.log('App.tsx useEffect - Extracted token:', verificationToken ? verificationToken.substring(0, 10) + '...' : 'NONE');
    console.log('App.tsx useEffect - Token length:', verificationToken ? verificationToken.length : 0);
    
    if (verificationToken) {
      // Handle verification - set status immediately so UI shows
      console.log('✅ Verification token found in URL:', verificationToken.substring(0, 10) + '...');
      console.log('🚀 Calling handleEmailVerification...');
      setVerificationStatus('verifying'); // Set immediately to show UI
      handleEmailVerification(verificationToken);
      // Clean up URL - remove both path and query params (but only after verification starts)
      // Don't clean immediately - wait to see if verification succeeds
    } else {
      console.log('❌ No verification token found in URL');
      // Check for existing auth token on app load
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');
      
      if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData);
          const decodedToken = decodeJWT(token);
          
          const isSuperUser = decodedToken?.is_super_user || false;
          const isSpecialUser = decodedToken?.user_type === 1 || decodedToken?.user_type === 2;
          
          setUser({
            id: isSpecialUser ? parsedUser.user_id : parsedUser.organization_id,
            name: `${parsedUser.first_name} ${parsedUser.last_name}`,
            email: parsedUser.email,
            organization: parsedUser.organization,
            token: token,
            is_super_user: isSuperUser,
            user_type: decodedToken?.user_type || 0,
            assigned_organizations: parsedUser.assigned_organizations || [],
          });
          setIsAuthenticated(true);
          
          // Set organization selection based on user type
          if (isSuperUser || decodedToken?.user_type === 1) {
            // SuperUsers start with no organization selected
            setSelectedOrganizationId(null);
            setSelectedOrganizationName(null);
          } else if (decodedToken?.user_type === 2) {
            // ProtocolManagers start with no organization selected
            setSelectedOrganizationId(null);
            setSelectedOrganizationName(null);
          } else {
            // Normal users automatically get their organization selected
            setSelectedOrganizationId(parsedUser.organization_id);
            setSelectedOrganizationName(parsedUser.organization);
          }
        } catch (error) {
          console.error('Error parsing stored user data:', error);
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
        }
      }
    }
    console.log('=== EMAIL VERIFICATION DEBUG END ===');
  }, [handleEmailVerification]);

  const handleLogin = async (email: string, password: string) => {
    setAuthLoading(true);
    setAuthError('');
    try {
      const response = await fetch(`${API_ENDPOINTS.AUTH}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }
      const data = await response.json();
      const decodedToken = decodeJWT(data.token);
      
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('userData', JSON.stringify(data.user));
      
      // Handle different user types
      const isSpecialUser = decodedToken?.user_type === 1 || decodedToken?.user_type === 2;
      
      setUser({
        id: isSpecialUser ? data.user.user_id : data.user.organization_id,
        name: `${data.user.first_name} ${data.user.last_name}`,
        email: data.user.email,
        organization: data.user.organization,
        token: data.token,
        is_super_user: decodedToken?.is_super_user || false,
        user_type: decodedToken?.user_type || 0,
        assigned_organizations: data.user.assigned_organizations || [],
      });
      setIsAuthenticated(true);
      
      // Set organization selection based on user type
      if (decodedToken?.is_super_user || decodedToken?.user_type === 1) {
        // SuperUsers start with no organization selected
        setSelectedOrganizationId(null);
        setSelectedOrganizationName(null);
      } else if (decodedToken?.user_type === 2) {
        // ProtocolManagers start with no organization selected
        setSelectedOrganizationId(null);
        setSelectedOrganizationName(null);
      } else {
        // Normal users automatically get their organization selected
        setSelectedOrganizationId(data.user.organization_id);
        setSelectedOrganizationName(data.user.organization);
      }
    } catch (error) {
      console.error('Login error:', error);
      setAuthError(error instanceof Error ? error.message : 'Network error. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignup = async (signupData: {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    organization: string;
    mobile?: string;
    referred_by?: string;
    engagement_level?: number;
  }) => {
    setAuthLoading(true);
    setAuthError('');
    try {
      const response = await fetch(`${API_ENDPOINTS.AUTH}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signupData),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const apiMessage = errorData?.message || 'Registration failed';
        const isDuplicateEmail =
          response.status === 409 ||
          (typeof apiMessage === 'string' && apiMessage.toLowerCase().includes('already exists'));
        const isGenericServerError =
          response.status >= 500 ||
          (typeof apiMessage === 'string' && apiMessage.toLowerCase() === 'internal server error');

        let userMessage = apiMessage;
        if (isDuplicateEmail) {
          userMessage = 'The email you are using to create your account is already in use.';
        } else if (isGenericServerError) {
          userMessage = 'We could not create your account right now. Please try again in a minute.';
        }

        setAuthError(userMessage);
        throw new Error(userMessage);
      }
      const data = await response.json();
      setAuthError('');
      
      // Registration successful - user needs to verify email
      // Return the signup data and message to show verification notice
      return {
        success: true,
        message: data.message || 'Registration successful. Please check your email to verify your account.',
        email: signupData.email,
      };
    } catch (error) {
      console.error('Signup error:', error);
      if (error instanceof Error && error.message) {
        setAuthError(error.message);
      } else {
        setAuthError('Registration failed. Please try again.');
      }
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };

  const handleOrganizationSelected = (organizationId: string, organizationName: string) => {
    setSelectedOrganizationId(organizationId);
    setSelectedOrganizationName(organizationName);
  };

  const handleLogout = () => {
    // Clear all localStorage to ensure clean logout
    localStorage.clear();
    setUser(null);
    setIsAuthenticated(false);
    setSelectedOrganizationId(null);
    setSelectedOrganizationName(null);
  };

  // Show verification status message FIRST (before checking authentication)
  // This ensures the verification UI shows even when user is not authenticated
  if (verificationStatus === 'verifying') {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ fontSize: '24px', marginBottom: '20px' }}>⏳</div>
        <div>Verifying your email address...</div>
      </div>
    );
  }

  if (verificationStatus === 'error') {
    return (
      <MantineProvider theme={theme}>
        <LandingPage
          onLogin={handleLogin}
          onSignup={handleSignup}
          isLoading={false}
          error={verificationMessage || 'Email verification failed. Please try again or request a new verification email.'}
        />
      </MantineProvider>
    );
  }

  if (!isAuthenticated) {
    return (
      <LandingPage
        onLogin={handleLogin}
        onSignup={handleSignup}
        isLoading={authLoading}
        error={authError}
      />
    );
  }

  // Special User flow: Show organization selector if no organization is selected
  if ((user?.is_super_user || user?.user_type === 1 || user?.user_type === 2) && !selectedOrganizationId) {
    return (
      <MantineProvider theme={theme}>
        <OrganizationSelector
          userToken={user.token}
          onOrganizationSelected={handleOrganizationSelected}
          onLogout={handleLogout}
          isSuperUser={user?.is_super_user || false}
          userEmail={user?.email}
        />
      </MantineProvider>
    );
  }

  // Regular user flow or Super User with selected organization
  return (
    <MantineProvider theme={theme}>
      <Dashboard
        user={user!}
        onLogout={handleLogout}
        selectedOrganizationId={selectedOrganizationId}
        selectedOrganizationName={selectedOrganizationName}
        isSuperUser={user?.is_super_user || false}
        userType={user?.user_type || 0}
      />
    </MantineProvider>
  );
}

export default App;
